import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { IItemForGetShortInfoWithStatus, EntityStatus } from 'types';
import { SHOW_ARCHIVED_RESTAURANTS } from 'appConstants';
import { Checkbox } from 'antd';
import Text from 'components/Text/Text';
import { ArchivedLabel } from 'components/Select/ArchivedLabel';
import SearchField from 'components/SearchField/SearchField';
import FormSelect from 'components/Select/FormSelect';
import style from './MultipleSelect.module.scss';

interface IProps {
	data: IItemForGetShortInfoWithStatus[];
	isLoading: boolean;
	refetch: () => void;
	responseError: boolean;
	isApplyToOne: boolean;
	setSelectedRestaurants: (value: string[]) => void;
	selectedRestaurants: string[];
}

export const MultipleSelect: React.FC<IProps> = React.memo(
	({ data, isLoading, refetch, responseError, isApplyToOne, setSelectedRestaurants, selectedRestaurants }) => {
		const [searchString, setSearchString] = useState<string>('');
		const [isSelectedAllRestaurants, setIsSelectedAllRestaurants] = useState<boolean>(false);
		const [showArchived, setShowArchived] = useState<boolean>(
			localStorage.getItem(SHOW_ARCHIVED_RESTAURANTS) === 'true',
		);

		const visibleData = useMemo(() => {
			const items = showArchived
				? data
				: data.filter((el) => el.status === EntityStatus.Active || selectedRestaurants.includes(el.id));

			return items.map((el) => ({
				...el,
				additionalContent: el.status === EntityStatus.Archived ? <ArchivedLabel /> : undefined,
			}));
		}, [data, showArchived, selectedRestaurants]);

		const filteredRestaurants = useMemo(() => {
			return searchString
				? visibleData.filter((el) => el.name.toLowerCase().includes(searchString.toLowerCase()))
				: visibleData;
		}, [searchString, visibleData]);

		const toggleShowArchived = useCallback(() => {
			localStorage.setItem(SHOW_ARCHIVED_RESTAURANTS, JSON.stringify(!showArchived));
			setShowArchived((prevValue) => !prevValue);
		}, [showArchived]);

		const handleSelectAllRestaurants = () => {
			if (isSelectedAllRestaurants) {
				setSelectedRestaurants([]);
			} else {
				const IDsOfAllRestaurants = visibleData.map((el) => el.id);
				setSelectedRestaurants(IDsOfAllRestaurants);
			}
		};

		useEffect(() => {
			setSearchString('');
		}, [isApplyToOne]);

		useEffect(() => {
			if (selectedRestaurants.length === 0) {
				setIsSelectedAllRestaurants(false);
			} else if (selectedRestaurants.length === visibleData.length) {
				setIsSelectedAllRestaurants(true);
			} else if (selectedRestaurants.length !== visibleData.length) {
				setIsSelectedAllRestaurants(false);
			}
		}, [selectedRestaurants.length, visibleData.length]);

		const renderSelectMenu = (menu: React.ReactElement) => {
			return (
				<>
					<div className={style.selectMenuHeader}>
						<Checkbox
							className={style.checkBox}
							checked={isSelectedAllRestaurants}
							disabled={!visibleData.length}
							onChange={handleSelectAllRestaurants}>
							<Text
								translationNameSpace="form"
								translationKey="selectAnyItemPlaceholder"
								asFragment={true}
							/>
						</Checkbox>
						<Checkbox className={style.checkBox} checked={showArchived} onChange={toggleShowArchived}>
							<Text translationNameSpace="modals" translationKey="showArchived" asFragment={true} />
						</Checkbox>
						<SearchField
							onChangeSelectSearchString={setSearchString}
							searchString={searchString}
							className={style.searchField}
						/>
					</div>
					{menu}
				</>
			);
		};

		return (
			<FormSelect
				testId="MultipleSelect"
				name="selectedRestaurants"
				label="restaurantsLabel"
				options={filteredRestaurants}
				fetchError={responseError}
				loading={isLoading}
				onRefetch={refetch}
				disabled={isApplyToOne || isLoading}
				onChange={setSelectedRestaurants}
				counterValue={selectedRestaurants.length}
				dropdownRender={renderSelectMenu}
				mode="multiple"
			/>
		);
	},
);
